/**
 * UnifiedUploadProgress - Batch upload progress display
 * 
 * Shows:
 * - Overall batch progress summary
 * - Individual file status cards
 * - Transaction counts
 * - Processing stages
 */

import React from 'react';
import { 
  FileText, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  RefreshCw,
  FileSpreadsheet,
  Sparkles,
  Cloud,
  BarChart3
} from 'lucide-react';
import type { FileUploadState } from '../../hooks/useUnifiedUpload';

interface UnifiedUploadProgressProps {
  files: FileUploadState[];
  summary?: {
    total: number;
    completed: number;
    processing: number;
    failed: number;
    successRate: string;
  };
  totalTransactions?: number;
  onRetry?: (index: number) => void;
  showDetails?: boolean;
  compact?: boolean;
}

const STAGE_LABELS: Record<string, string> = {
  'downloading': 'Downloading from S3...',
  'fingerprinting': 'Analyzing document...',
  'messiness_check': 'Checking quality...',
  'pdfplumber': 'Extracting with PDFPlumber...',
  'pdfplumber_cached': 'Using cached rules...',
  'claude_ocr': 'AI Processing (Claude)...',
  'textract': 'AWS Textract OCR...',
  'csv_parsing': 'Parsing CSV...',
  'excel_parsing': 'Parsing Excel...',
  'complete': 'Complete!',
  'error': 'Failed',
};

function getStatusIcon(status: string, stage?: string | null) {
  switch (status) {
    case 'parsed':
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    case 'failed':
      return <XCircle className="w-5 h-5 text-red-500" />;
    case 'processing':
      if (stage?.includes('claude')) return <Sparkles className="w-5 h-5 text-purple-500 animate-pulse" />;
      if (stage?.includes('textract')) return <Cloud className="w-5 h-5 text-blue-500 animate-pulse" />;
      return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
    case 'uploading':
      return <Cloud className="w-5 h-5 text-blue-500 animate-pulse" />;
    default:
      return <FileText className="w-5 h-5 text-gray-400" />;
  }
}

export default function UnifiedUploadProgress({ 
  files, 
  summary,
  totalTransactions = 0,
  onRetry,
  showDetails = true,
  compact = false,
}: UnifiedUploadProgressProps) {
  if (files.length === 0) return null;

  const overallProgress = files.length > 0
    ? Math.round(files.reduce((sum, f) => sum + f.progress, 0) / files.length)
    : 0;

  return (
    <div className="space-y-4">
      {/* Overall Progress Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-gray-900">
              Batch Upload Progress
            </span>
          </div>
          {summary && (
            <div className="flex items-center gap-4 text-sm">
              <span className="text-green-600 font-medium">
                ✓ {summary.completed}
              </span>
              <span className="text-blue-600">
                ⏳ {summary.processing}
              </span>
              {summary.failed > 0 && (
                <span className="text-red-600">
                  ✗ {summary.failed}
                </span>
              )}
              <span className="text-gray-500">
                / {summary.total} files
              </span>
            </div>
          )}
        </div>

        {/* Overall Progress Bar */}
        <div className="h-3 bg-blue-100 rounded-full overflow-hidden mb-2">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500 ease-out"
            style={{ width: `${overallProgress}%` }}
          />
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-blue-700 font-medium">{overallProgress}% complete</span>
          {totalTransactions > 0 && (
            <span className="text-indigo-600 font-medium">
              {totalTransactions.toLocaleString()} transactions extracted
            </span>
          )}
        </div>
      </div>

      {/* Individual File Progress */}
      {showDetails && (
        <div className={`space-y-2 ${compact ? 'max-h-64 overflow-y-auto' : ''}`}>
          {files.map((file, index) => (
            <div 
              key={file.file.name + index}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                file.status === 'parsed' 
                  ? 'bg-green-50 border-green-200' 
                  : file.status === 'failed'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-white border-gray-200'
              }`}
            >
              {/* Status Icon */}
              {getStatusIcon(file.status)}

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 truncate text-sm">
                    {file.file.name}
                  </span>
                  <span className="text-xs text-gray-400">
                    ({(file.file.size / 1024).toFixed(0)} KB)
                  </span>
                </div>

                {/* Status Text */}
                <div className="text-xs text-gray-500">
                  {file.status === 'parsed' && (
                    <span className="text-green-600">
                      ✓ {file.transactionsCount || file.result?.transactions?.length || 0} transactions
                      {file.retryCount && file.retryCount > 0 && (
                        <span className="text-gray-400 ml-1">(after retry)</span>
                      )}
                    </span>
                  )}
                  {file.status === 'failed' && (
                    <span className="text-red-600">
                      {file.error || 'Processing failed'}
                      {file.retryCount && file.retryCount > 0 && (
                        <span className="text-gray-500 ml-1">(retried {file.retryCount}x)</span>
                      )}
                    </span>
                  )}
                  {file.status === 'processing' && (
                    <span className="text-blue-600">
                      {file.retryCount && file.retryCount > 0 ? 'Retrying... ' : ''}{file.progress}%
                    </span>
                  )}
                  {file.status === 'uploading' && (
                    <span className="text-blue-600">Uploading to S3...</span>
                  )}
                </div>
              </div>

              {/* Progress or Actions */}
              <div className="flex items-center gap-2">
                {file.status === 'processing' && (
                  <div className="w-16 h-1.5 bg-blue-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 transition-all duration-300"
                      style={{ width: `${file.progress}%` }}
                    />
                  </div>
                )}
                {file.status === 'failed' && onRetry && (
                  <button
                    onClick={() => onRetry(index)}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-100 rounded border border-red-200"
                    title="Retry processing this file"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Retry
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {summary && summary.processing === 0 && (
        <div className={`p-3 rounded-lg ${
          summary.failed === 0 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {summary.failed === 0 ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertTriangle className="w-5 h-5" />
              )}
              <span className="font-medium">
                {summary.failed === 0 
                  ? `All ${summary.total} files processed successfully!`
                  : `${summary.completed} of ${summary.total} files processed (${summary.failed} failed)`
                }
              </span>
            </div>
            {totalTransactions > 0 && (
              <span className="font-semibold">
                {totalTransactions.toLocaleString()} total transactions
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
